import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  useDisclosure,
  Heading,
  IconButton,
  HStack,
  useToast,
  Spinner,
  Center,
  Switch,
  Flex,
  Text,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  SimpleGrid,
  Select,
} from '@chakra-ui/react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/table';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/modal';
import {
  FormControl,
  FormLabel,
} from '@chakra-ui/form-control';
import { FaEdit, FaTrash, FaPlus, FaCog, FaSignInAlt, FaUsers, FaSearch } from 'react-icons/fa';

const INSTANCE_SEARCH_STORAGE_KEY = 'licenseManager.instanceSearch';

const ADMIN_TYPE_LABELS = {
  event_host: 'Event Host',
  event_instance_host: 'Event Instance Host',
  course_tutor: 'Course Tutor',
  account_admin: 'Account Admin',
  system_administrator: 'System Administrator',
};

const ADMIN_TYPE_ORDER = ['event_host', 'event_instance_host', 'course_tutor', 'account_admin', 'system_administrator'];

const ADMIN_TYPE_COLORS = {
  event_host: 'gray',
  event_instance_host: 'teal',
  course_tutor: 'blue',
  account_admin: 'purple',
  system_administrator: 'red',
};

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const Instances = () => {
  const [instances, setInstances] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isLimitsOpen, onOpen: onLimitsOpen, onClose: onLimitsClose } = useDisclosure();
  const { isOpen: isAdminUsersOpen, onOpen: onAdminUsersOpen, onClose: onAdminUsersClose } = useDisclosure();
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [loginAsUserId, setLoginAsUserId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
    is_lite: false,
    registration_enabled: true,
    listed_on_portal: true,
    vertical: '',
  });
  const [limitsData, setLimitsData] = useState({
    max_organisers: 1000,
    max_attendees: 1000,
    max_regions: 1000,
    max_events: 1000,
    max_courses: 1000,
    max_course_sessions: 1000,
    courses_enabled: false,
    events_enabled: true,
    social_enabled: false,
    sso_enabled: false,
    webhooks_enabled: false,
    mobile_enabled: false,
    ticketing_enabled: false,
    assistant_enabled: false,
  });
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => {
    try {
      return localStorage.getItem(INSTANCE_SEARCH_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  });
  const toast = useToast();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    try {
      localStorage.setItem(INSTANCE_SEARCH_STORAGE_KEY, value);
    } catch (_) {
      // localStorage unavailable — search still works for this session
    }
  };

  const filteredInstances = instances.filter((instance) =>
    (instance.name || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/instances/', {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setInstances(data);
      } else {
        toast({
          title: 'Error fetching instances',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error fetching instances',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchVerticals = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/verticals/', {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setVerticals(data);
      }
    } catch (_) {
      // verticals are optional — silently ignore
    }
  }, []);

  useEffect(() => {
    fetchInstances();
    fetchVerticals();
  }, [fetchInstances, fetchVerticals]);

  const handleEdit = (instance) => {
    setSelectedInstance(instance);
    setFormData({
      name: instance.name || '',
      description: instance.description || '',
      contact_email: instance.contact_email || '',
      is_lite: instance.is_lite || false,
      registration_enabled: instance.registration_enabled !== false,
      listed_on_portal: instance.listed_on_portal !== false,
      vertical: instance.vertical || '',
    });
    onOpen();
  };

  const handleAdd = () => {
    setSelectedInstance(null);
    setFormData({
      name: '',
      description: '',
      contact_email: '',
      is_lite: false,
      registration_enabled: true,
    listed_on_portal: true,
      vertical: '',
    });
    onOpen();
  };

  const handleDelete = async (instance) => {
    if (!window.confirm(`Are you sure you want to delete "${instance.name}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/v1/instances/${instance.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
      });
      if (response.ok) {
        toast({
          title: 'Instance deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchInstances();
      } else {
        toast({
          title: 'Error deleting instance',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error deleting instance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleLoginAsAdmin = async (instance) => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/v1/instances/${instance.id}/login_as_admin/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
      });
      const data = await response.json();

      if (response.ok && data.success) {
        window.open(data.data.login_url, '_blank', 'noopener,noreferrer');
      } else {
        toast({
          title: 'Login failed',
          description: data.data || 'Could not generate a login link.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openAdminUsersModal = async (instance) => {
    setSelectedInstance(instance);
    setAdminUsers([]);
    onAdminUsersOpen();
    setAdminUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/instances/${instance.id}/admin_users/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      const data = await response.json();

      if (response.ok && data.success) {
        const sorted = [...data.data].sort((a, b) => {
          const rankDiff = ADMIN_TYPE_ORDER.indexOf(a.admin_type) - ADMIN_TYPE_ORDER.indexOf(b.admin_type);
          if (rankDiff !== 0) return rankDiff;
          return (a.name || '').localeCompare(b.name || '');
        });
        setAdminUsers(sorted);
      } else {
        toast({
          title: 'Failed to load admin users',
          description: data.data || 'Could not fetch admin users for this instance.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to load admin users',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAdminUsersLoading(false);
    }
  };

  const handleLoginAsUser = async (adminUser) => {
    if (!selectedInstance) return;
    setLoginAsUserId(adminUser.id);
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/v1/instances/${selectedInstance.id}/login_as_user/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify({ profile_id: adminUser.id }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        window.open(data.data.login_url, '_blank', 'noopener,noreferrer');
      } else {
        toast({
          title: 'Login failed',
          description: data.data || 'Could not generate a login link.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoginAsUserId(null);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const url = selectedInstance
        ? `/api/v1/instances/${selectedInstance.id}/`
        : '/api/v1/instances/';
      const method = selectedInstance ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: `Instance ${selectedInstance ? 'updated' : 'created'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
        setSelectedInstance(null);
        fetchInstances();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error saving instance',
          description: JSON.stringify(errorData),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error saving instance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLimitsChange = (name, value) => {
    setLimitsData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLimitsSwitchChange = (e) => {
    const { name, checked } = e.target;
    setLimitsData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const openLimitsModal = async (instance) => {
    setSelectedInstance(instance);
    setLimitsLoading(true);
    onLimitsOpen();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/instances/${instance.id}/limits/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setLimitsData({
          max_organisers: data.max_organisers || 1000,
          max_attendees: data.max_attendees || 1000,
          max_regions: data.max_regions || 1000,
          max_events: data.max_events || 1000,
          max_courses: data.max_courses || 1000,
          max_course_sessions: data.max_course_sessions || 1000,
          courses_enabled: data.courses_enabled || false,
          events_enabled: data.events_enabled !== false,
          social_enabled: data.social_enabled || false,
          sso_enabled: data.sso_enabled || false,
          webhooks_enabled: data.webhooks_enabled || false,
          mobile_enabled: data.mobile_enabled || false,
          ticketing_enabled: data.ticketing_enabled || false,
          assistant_enabled: data.assistant_enabled || false,
        });
      }
    } catch (error) {
      toast({
        title: 'Error fetching limits',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLimitsLoading(false);
    }
  };

  const handleSaveLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      
      const response = await fetch(`/api/v1/instances/${selectedInstance.id}/limits/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(limitsData),
      });

      if (response.ok) {
        toast({
          title: 'Limits updated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onLimitsClose();
        fetchInstances();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error saving limits',
          description: JSON.stringify(errorData),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error saving limits',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Instances</Heading>
          <Text color="gray.500" mt={1}>Manage your application instances</Text>
        </Box>
        <Button
          leftIcon={<FaPlus />}
          bgGradient="linear(to-r, purple.500, purple.600)"
          color="white"
          _hover={{
            bgGradient: "linear(to-r, purple.600, purple.700)",
            transform: "translateY(-2px)",
            boxShadow: "lg",
          }}
          onClick={handleAdd}
          borderRadius="lg"
          px={6}
        >
          Add Instance
        </Button>
      </Flex>

      <InputGroup maxW="320px" mb={4}>
        <InputLeftElement pointerEvents="none">
          <Box as={FaSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by instance name..."
          value={searchTerm}
          onChange={handleSearchChange}
          bg="white"
          borderRadius="lg"
          focusBorderColor="purple.500"
        />
      </InputGroup>

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.100"
        overflow="hidden"
      >
        <Box overflowX="auto">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">Description</Th>
              <Th color="gray.600">Contact Email</Th>
              <Th color="gray.600">Type</Th>
              <Th color="gray.600">Vertical</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredInstances.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                  {instances.length === 0
                    ? 'No instances found. Click "Add Instance" to create one.'
                    : 'No instances match your search.'}
                </Td>
              </Tr>
            ) : (
              filteredInstances.map((instance) => (
                <Tr
                  key={instance.id}
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.2s"
                >
                  <Td fontWeight="medium" color="gray.800">{instance.name}</Td>
                  <Td color="gray.600" maxW="300px" isTruncated>
                    {instance.description?.substring(0, 50)}{instance.description?.length > 50 ? '...' : ''}
                  </Td>
                  <Td color="gray.600">{instance.contact_email || '-'}</Td>
                  <Td>
                    <Badge
                      colorScheme={instance.is_lite ? 'orange' : 'green'}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {instance.is_lite ? 'Lite' : 'Full'}
                    </Badge>
                  </Td>
                  <Td>
                    {instance.vertical ? (
                      <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                        {verticals.find((v) => v.id === instance.vertical)?.name || `#${instance.vertical}`}
                      </Badge>
                    ) : (
                      <Text color="gray.400" fontSize="sm">—</Text>
                    )}
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <IconButton
                        aria-label="Log in as admin"
                        icon={<FaSignInAlt />}
                        size="sm"
                        variant="ghost"
                        colorScheme="green"
                        onClick={() => handleLoginAsAdmin(instance)}
                      />
                      <IconButton
                        aria-label="View admin users"
                        icon={<FaUsers />}
                        size="sm"
                        variant="ghost"
                        colorScheme="teal"
                        onClick={() => openAdminUsersModal(instance)}
                      />
                      <IconButton
                        aria-label="Configure limits"
                        icon={<FaCog />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => openLimitsModal(instance)}
                      />
                      <IconButton
                        aria-label="Edit instance"
                        icon={<FaEdit />}
                        size="sm"
                        variant="ghost"
                        colorScheme="purple"
                        onClick={() => handleEdit(instance)}
                      />
                      <IconButton
                        aria-label="Delete instance"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(instance)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        </Box>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            {selectedInstance ? 'Edit' : 'Add New'} Instance
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium">Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel fontWeight="medium">Description</FormLabel>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
                rows={4}
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Contact Email</FormLabel>
              <Input
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
              />
            </FormControl>
            <Flex mt={6} gap={8} wrap="wrap">
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Lite Instance</FormLabel>
                <Switch
                  name="is_lite"
                  isChecked={formData.is_lite}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Registration Enabled</FormLabel>
                <Switch
                  name="registration_enabled"
                  isChecked={formData.registration_enabled}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontWeight="medium">Show on Portal Homepage</FormLabel>
                <Switch
                  name="listed_on_portal"
                  aria-label="Show on Portal Homepage"
                  isChecked={formData.listed_on_portal}
                  onChange={handleInputChange}
                  colorScheme="purple"
                />
              </FormControl>
            </Flex>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Vertical</FormLabel>
              <Select
                name="vertical"
                value={formData.vertical || ''}
                onChange={handleInputChange}
                borderRadius="lg"
                focusBorderColor="purple.500"
                placeholder="No vertical assigned"
              >
                {verticals.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, purple.600, purple.700)",
              }}
              onClick={handleSave}
              borderRadius="lg"
              px={6}
            >
              {selectedInstance ? 'Update' : 'Create'} Instance
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Admin Users Modal */}
      <Modal isOpen={isAdminUsersOpen} onClose={onAdminUsersClose} size="2xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            <Flex align="center">
              <FaUsers style={{ marginRight: '12px' }} />
              Admin Users: {selectedInstance?.name}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <Text color="gray.600" fontSize="sm" mb={4}>
              Every admin-type user on this instance, from event host up to system administrator.
              Use "Login as" to open the dashboard as that user — for verifying permissions or
              debugging a support issue.
            </Text>
            {adminUsersLoading ? (
              <Center py={8}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
              </Center>
            ) : adminUsers.length === 0 ? (
              <Text textAlign="center" py={8} color="gray.500">
                No admin-type users found for this instance.
              </Text>
            ) : (
              <Box maxH="60vh" overflowY="auto">
                <Table size="sm">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th textAlign="right">Action</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {adminUsers.map((adminUser) => (
                      <Tr key={adminUser.id}>
                        <Td fontWeight="medium" color="gray.800">{adminUser.name || '-'}</Td>
                        <Td color="gray.600">{adminUser.email || '-'}</Td>
                        <Td>
                          <Badge
                            colorScheme={ADMIN_TYPE_COLORS[adminUser.admin_type] || 'gray'}
                            borderRadius="full"
                            px={3}
                            py={1}
                          >
                            {ADMIN_TYPE_LABELS[adminUser.admin_type] || adminUser.admin_type}
                          </Badge>
                        </Td>
                        <Td>
                          {adminUser.blocked ? (
                            <Badge colorScheme="red" borderRadius="full" px={3} py={1}>Blocked</Badge>
                          ) : adminUser.active ? (
                            <Badge colorScheme="green" borderRadius="full" px={3} py={1}>Active</Badge>
                          ) : (
                            <Badge colorScheme="gray" borderRadius="full" px={3} py={1}>Inactive</Badge>
                          )}
                        </Td>
                        <Td textAlign="right">
                          <Button
                            size="sm"
                            leftIcon={<FaSignInAlt />}
                            colorScheme="green"
                            variant="outline"
                            isLoading={loginAsUserId === adminUser.id}
                            onClick={() => handleLoginAsUser(adminUser)}
                          >
                            Login as
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>
          <ModalFooter borderTopWidth="1px" py={4}>
            <Button onClick={onAdminUsersClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Limits Modal */}
      <Modal isOpen={isLimitsOpen} onClose={onLimitsClose} size="xl">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            <Flex align="center">
              <FaCog style={{ marginRight: '12px' }} />
              Limits & Capabilities: {selectedInstance?.name}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {limitsLoading ? (
              <Center py={8}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
              </Center>
            ) : (
              <>
                <Text fontWeight="semibold" color="gray.700" mb={4}>
                  Resource Limits
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">Max Organisers</FormLabel>
                    <NumberInput
                      value={limitsData.max_organisers}
                      onChange={(_, val) => handleLimitsChange('max_organisers', val)}
                      min={0}
                      max={100000}
                    >
                      <NumberInputField borderRadius="lg" focusBorderColor="purple.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">Max Attendees</FormLabel>
                    <NumberInput
                      value={limitsData.max_attendees}
                      onChange={(_, val) => handleLimitsChange('max_attendees', val)}
                      min={0}
                      max={100000}
                    >
                      <NumberInputField borderRadius="lg" focusBorderColor="purple.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">Max Regions</FormLabel>
                    <NumberInput
                      value={limitsData.max_regions}
                      onChange={(_, val) => handleLimitsChange('max_regions', val)}
                      min={0}
                      max={100000}
                    >
                      <NumberInputField borderRadius="lg" focusBorderColor="purple.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">Max Events</FormLabel>
                    <NumberInput
                      value={limitsData.max_events}
                      onChange={(_, val) => handleLimitsChange('max_events', val)}
                      min={0}
                      max={100000}
                    >
                      <NumberInputField borderRadius="lg" focusBorderColor="purple.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">Max Courses</FormLabel>
                    <NumberInput
                      value={limitsData.max_courses}
                      onChange={(_, val) => handleLimitsChange('max_courses', val)}
                      min={0}
                      max={100000}
                    >
                      <NumberInputField borderRadius="lg" focusBorderColor="purple.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">Max Course Sessions</FormLabel>
                    <NumberInput
                      value={limitsData.max_course_sessions}
                      onChange={(_, val) => handleLimitsChange('max_course_sessions', val)}
                      min={0}
                      max={100000}
                    >
                      <NumberInputField borderRadius="lg" focusBorderColor="purple.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>

                <Divider my={6} />

                <Text fontWeight="semibold" color="gray.700" mb={4}>
                  Feature Capabilities
                </Text>
                <SimpleGrid columns={2} spacing={4}>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.events_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.events_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">Events</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable events functionality</Text>
                      </Box>
                      <Switch
                        name="events_enabled"
                        isChecked={limitsData.events_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.courses_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.courses_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">Courses</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable courses functionality</Text>
                      </Box>
                      <Switch
                        name="courses_enabled"
                        isChecked={limitsData.courses_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.social_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.social_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">Social</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable social functionality</Text>
                      </Box>
                      <Switch
                        name="social_enabled"
                        isChecked={limitsData.social_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.sso_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.sso_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">SSO Integrations</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable Microsoft Entra SSO configuration</Text>
                      </Box>
                      <Switch
                        name="sso_enabled"
                        isChecked={limitsData.sso_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.webhooks_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.webhooks_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">Webhooks</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable webhook integrations</Text>
                      </Box>
                      <Switch
                        name="webhooks_enabled"
                        isChecked={limitsData.webhooks_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.ticketing_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.ticketing_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">Ticketing Portal</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable public ticketing at ticketing.selwynevents.com</Text>
                      </Box>
                      <Switch
                        name="ticketing_enabled"
                        isChecked={limitsData.ticketing_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.mobile_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.mobile_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">Mobile App</FormLabel>
                        <Text fontSize="xs" color="gray.500">Show this instance in the mobile app</Text>
                      </Box>
                      <Switch
                        name="mobile_enabled"
                        isChecked={limitsData.mobile_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={limitsData.assistant_enabled ? 'green.300' : 'gray.200'}
                    bg={limitsData.assistant_enabled ? 'green.50' : 'white'}
                  >
                    <FormControl display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <FormLabel mb="0" fontWeight="medium">AI Assistant</FormLabel>
                        <Text fontSize="xs" color="gray.500">Enable the self-hosted chat assistant for event Q&A</Text>
                      </Box>
                      <Switch
                        name="assistant_enabled"
                        isChecked={limitsData.assistant_enabled}
                        onChange={handleLimitsSwitchChange}
                        colorScheme="green"
                        size="lg"
                      />
                    </FormControl>
                  </Box>
                </SimpleGrid>
              </>
            )}
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onLimitsClose} borderRadius="lg">
              Cancel
            </Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, purple.600, purple.700)",
              }}
              onClick={handleSaveLimits}
              isDisabled={limitsLoading}
              borderRadius="lg"
              px={6}
            >
              Save Limits
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Instances;
